import React, { useState } from 'react';
import styled from 'styled-components';
import { Ellipsis, GetColor, ItemIcon, ItemType, RelativePortal } from 'venn-ui-kit';
import ModalFooter from '../modal/ModalFooter';
import type { AnalysisSubject } from 'venn-utils';
import NumberInput from '../input/NumberInput';

type FeesRelativePopoverProps = {
  setFee: (fee: number) => void;
  fee?: number;
  onClose: () => void;
  analysisSubject: AnalysisSubject;
};

export const FeesRelativePopover: React.FunctionComponent<React.PropsWithChildren<FeesRelativePopoverProps>> = ({
  setFee,
  fee,
  onClose,
  analysisSubject,
}) => {
  const [value, setValue] = useState((fee ?? 0) * 100.0);
  return (
    <RelativePortal>
      <PortalContainer>
        <HeadingContainer>Advisory Fee</HeadingContainer>
        <BodyContainer data-testid="qa-fee-container">
          <NameContainer>
            <SubjectIcon subject={analysisSubject} />
            &nbsp;
            <Ellipsis>{analysisSubject.name}</Ellipsis>
          </NameContainer>
          <InputContainer>
            <FeesInput
              data-testid="qa-fees-input"
              min={0}
              precision={2}
              step={0.1}
              onChange={(value: number) => setValue(value)}
              formatter={(value) => `${value} %`}
              value={value}
            />
          </InputContainer>
        </BodyContainer>
        <ModalFooter
          primaryLabel="Save"
          onPrimaryClick={() => {
            setFee(Number(value) / 100);
            onClose();
          }}
          secondaryLabel="Cancel"
          onSecondaryClick={() => {
            setValue((fee ?? 0) * 100.0);
            onClose();
          }}
          primaryDisabled={!Number(value) && value !== 0}
        />
      </PortalContainer>
    </RelativePortal>
  );
};

const StyledItemIcon = styled(ItemIcon)`
  margin-right: 2px;
`;

export const SubjectIcon = ({ subject }: { subject?: AnalysisSubject }) => (
  <StyledItemIcon
    item={
      subject?.portfolio
        ? subject?.strategy && subject.portfolio.id !== subject.strategy.id
          ? ItemType.Strategy
          : ItemType.Portfolio
        : ItemType.Investment
    }
    dataSource={subject?.superItem.dataSource || subject?.portfolio?.dataSource}
    isUploaded={subject?.userUploaded && !subject?.isCompositeBenchmark}
    investmentSource={subject?.fund?.investmentSource}
  />
);

const FeesInput = styled(NumberInput)`
  .rc-input-number-input {
    text-align: right;
    padding-right: 4px;
  }
`;

const HeadingContainer = styled.span`
  font-weight: bold;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: 16px;
  margin-left: 16px;
`;

const PortalContainer = styled.div`
  background: ${GetColor.White};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 2px 2px 2px 2px ${GetColor.LightGrey};
  min-height: 160px;
  min-width: 300px;
`;

const BodyContainer = styled.div`
  margin-top: 20px;
  margin-left: 16px;
  margin-right: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 140px;
  overflow-wrap: break-word;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
